import { useEffect, useState } from "react";
import { useRoute, useRouteQuery } from "./lib/router.js";
import { useTheme } from "./lib/theme.js";
import { api } from "./lib/api.js";
import type { MeResponse } from "../shared/api.js";
import { AppLogo, BlazeMark } from "./components/Panel.js";
import { SignInScreen } from "./screens/SignInScreen.js";
import { CheckEmailScreen } from "./screens/CheckEmailScreen.js";
import { ConfirmScreen } from "./screens/ConfirmScreen.js";
import { HomeScreen } from "./screens/HomeScreen.js";
import { HouseholdScreen } from "./screens/HouseholdScreen.js";
import { SettingsScreen } from "./screens/SettingsScreen.js";
import { OfflineScreen } from "./screens/OfflineScreen.js";

export function App() {
  const path = useRoute();
  const query = useRouteQuery();
  const { theme, setTheme } = useTheme();
  const [me, setMe] = useState<MeResponse | null>(null);
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    if (!navigator.onLine) {
      setAuthChecked(true);
      return;
    }
    api
      .me()
      .then(setMe)
      .catch(() => setMe(null))
      .finally(() => setAuthChecked(true));
  }, []);

  if (!authChecked) {
    return (
      <div className="min-h-dvh flex items-center justify-center shell-surface">
        <div className="flex flex-col items-center gap-3" role="status" aria-live="polite">
          <AppLogo />
          <p className="font-text text-granite-600 dark:text-granite-300">Checking in…</p>
        </div>
      </div>
    );
  }

  if (!navigator.onLine) {
    return <OfflineScreen />;
  }

  // Auth confirmation route carries a magic token in the URL fragment.
  if (path === "/confirm") {
    return <ConfirmScreen token={query.get("token") ?? ""} />;
  }

  if (path === "/sign-in" || path === "/login") {
    return <SignInScreen />;
  }
  if (path === "/check-email") {
    return <CheckEmailScreen />;
  }

  // Authenticated shell.
  if (!me) {
    return <SignInScreen />;
  }

  const route = (() => {
    switch (path) {
      case "/household":
        return <HouseholdScreen me={me} />;
      case "/settings":
        return <SettingsScreen me={me} onUpdate={setMe} theme={theme} setTheme={setTheme} />;
      default:
        return <HomeScreen me={me} onUpdate={setMe} />;
    }
  })();

  return (
    <div className="min-h-dvh shell-surface flex flex-col">
      <header className="px-[var(--spacing-gutter)] py-3 flex items-center justify-between border-b border-granite-200 dark:border-granite-700">
        <a href="#/" className="flex items-center gap-2.5 no-underline" aria-label="lingoLoco home">
          <AppLogo size={34} />
          <span className="font-display text-xl leading-none text-granite-900 dark:text-granite-50">
            lingoLoco
          </span>
        </a>
        <span className="font-text text-[14px] text-granite-500 dark:text-granite-300">
          {me.group.name}
        </span>
      </header>

      <main className="flex-1 w-full max-w-2xl mx-auto px-[var(--spacing-gutter)] py-6">{route}</main>

      <footer className="px-[var(--spacing-gutter)] py-4 flex items-center justify-between border-t border-granite-200 dark:border-granite-700">
        <nav aria-label="Primary" className="flex items-center gap-1">
          <NavLink href="#/" label="Home" active={path === "/"} />
          <NavLink href="#/household" label="Household" active={path === "/household"} />
          <NavLink href="#/settings" label="Settings" active={path === "/settings"} />
        </nav>
        <BlazeMark solid className="text-granite-400" />
      </footer>
    </div>
  );
}

function NavLink({ href, label, active }: { href: string; label: string; active: boolean }) {
  return (
    <a
      href={href}
      aria-current={active ? "page" : undefined}
      className={`min-h-[44px] inline-flex items-center px-3 rounded-[var(--radius-control)] font-text text-[15px] no-underline transition-colors ${
        active
          ? "text-blaze font-medium"
          : "text-granite-600 dark:text-granite-300 hover:bg-granite-100 dark:hover:bg-granite-800"
      }`}
    >
      {label}
    </a>
  );
}
